import { useCallback, useMemo } from "react";
import type { Animal } from "../data/animals";
import { allFlags } from "../data/worldcup";
import { AnimalCard } from "./AnimalCard";
import { useVoiceControl } from "../voice/VoiceProvider";
import { scrollPage } from "../lib/scroll";
import { heardAny } from "../voice/match";
import { useLocale, useT } from "../i18n/LocaleProvider";
import { aliasesFor, localizeAnimal } from "../i18n/localize";

interface Props {
  onPick: (flag: Animal) => void;
  onHome: () => void;
  completed: Set<string>;
}

export function FlagsPage({ onPick, onHome, completed }: Props) {
  const t = useT();
  const { locale } = useLocale();
  // `allFlags` is module data and never went through the localizer — localize
  // for DISPLAY, keep the original for onPick (App localizes on render).
  const flags = useMemo(
    () => allFlags.map((f) => ({ f, view: localizeAnimal(f, locale) })),
    [locale],
  );
  const onCommand = useCallback(
    (transcript: string): boolean => {
      // Navigation wins over scrolling: if a phrase contains both (e.g. the
      // recognizer groups a "down" you said while browsing with "home"), going
      // home is the real intent — checking scroll first would swallow "home".
      if (heardAny(transcript, [...t.commands.home, ...t.commands.back])) {
        onHome();
        return true;
      }
      if (heardAny(transcript, t.commands.up)) {
        scrollPage(-1);
        return true;
      }
      if (heardAny(transcript, t.commands.down)) {
        scrollPage(1);
        return true;
      }
      for (const f of allFlags) {
        if (heardAny(transcript, aliasesFor(localizeAnimal(f, locale), locale))) {
          onPick(f);
          return true;
        }
      }
      return false;
    },
    [onPick, onHome, t, locale],
  );
  useVoiceControl(onCommand);

  return (
    <div className="home">
      <header className="player-header">
        <button className="home-btn" onClick={onHome} aria-label={t.backToHome}>
          {t.home}
        </button>
        <h1>{t.flagsTitle}</h1>
      </header>
      <p className="subtitle">{t.flagsSubtitle}</p>

      <div className="card-grid">
        {flags.map(({ f, view }) => (
          <AnimalCard
            key={f.id}
            animal={view}
            done={completed.has(f.id)}
            onClick={() => onPick(f)}
          />
        ))}
      </div>
    </div>
  );
}
